import React, { useRef, useContext } from 'react'
import styled from '@emotion/styled'
import { message } from 'antd'
import { usePathname } from 'next/navigation'
import { LinkContext } from 'app/components/providers/linkProvider'
import { ReactComponent as FileIcon } from '../../images/svg/file.svg'
import { ReactComponent as CheckCircleIcon } from '../../images/svg/check-circle.svg'
import { ReactComponent as PdfSvg } from '../../images/svg/pdfIcon.svg'

import { getUrlSearchString } from '../utils'
import { maxFileSize, useUploadResumeContext } from './context'
import UploadResumeWrap from './Wrap.mobile'
import { Gtag } from 'helpers/gtagEvent'
import { Button } from 'app/(new-pages)/components/Button/pc/index.pc'
import { RouterContext } from '../routerProvider'

const UploadSuccess = (props: { lang: any; userDetail: any; getUserInfo: () => Promise<any> }) => {
  const { getUserInfo, userDetail, lang = {} } = props
  const profile = lang.profile || {}
  const inputRef = useRef(null)
  const { fileData, uploadResumeFile } = useUploadResumeContext()
  const { push } = useContext(LinkContext)
  const pathname = usePathname()

  const onChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }
    if (file.size > maxFileSize) {
      message.error(profile?.uploadResumeModal?.errorTip)
      return false
    }
    const resumeId = userDetail?.resume?.id || fileData?.data?.id
    uploadResumeFile({ file, resumeId: resumeId })
  }

  const handleUpload = () => {
    inputRef.current.click()
  }

  const handSubmit = () => {
    Gtag('register_upload_resume')
    setTimeout(() => {
      const searchWords = getUrlSearchString(1)
      getUserInfo && getUserInfo()
      push(`${pathname}/thank-you/${searchWords}`)
    }, 200)
  }

  const handleBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back()
    }
  }

  return (
    <UploadResumeWrap
      {...props}
      title='Upload your resume file'
      footer={
        <FooterWrap>
          <BackButton onClick={handleBack}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M5.25 13.5L1.5 9L5.25 4.5'
                stroke='#121212'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>{' '}
            <p className='countdownText'>{profile.back}</p>
          </BackButton>
          <div className='footer-btns'>
            <Button variant='primaryBtn' btnSize='size44' className='confirm' onClick={handSubmit}>
              {profile?.confirm}
            </Button>
          </div>
        </FooterWrap>
      }
    >
      <ContentWrap>
        <div className='modal-content'>
          <div className='file-info'>
            {fileData?.data?.name?.indexOf('word') > -1 ? WordPdf : <PdfSvg />}
            <span className='file-name'>{fileData?.data?.name || 'xxxxx.pdf'}</span>
            <div className='upload-success'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <circle cx='10.1373' cy='10.1973' r='5.61194' fill='white' />
                <path
                  d='M9.82123 16.4766C13.9509 16.4766 17.2987 13.1288 17.2987 8.99915C17.2987 4.86945 13.9509 1.52167 9.82123 1.52167C5.69153 1.52167 2.34375 4.86945 2.34375 8.99915C2.34375 13.1288 5.69153 16.4766 9.82123 16.4766ZM9.69346 11.7402C9.55414 11.8964 9.35573 11.987 9.14644 11.9901C8.93716 11.9932 8.73616 11.9085 8.5923 11.7564L6.01166 9.02938L7.09789 8.00147L9.11911 10.1374L12.7628 6.05197L13.8789 7.04739L9.69346 11.7402Z'
                  fill='#29CC6A'
                />
              </svg>
              {profile?.uploadResumeModal?.uploadSuccess}
            </div>
            <input
              type='file'
              accept='.pdf, .doc, .docx'
              ref={inputRef}
              onChange={onChange}
              style={{ display: 'none' }}
              hidden
            />
            <div className='file-upload'>
              <span onClick={handleUpload}>{profile?.uploadResumeModal?.reUploadBtn}</span>
            </div>
          </div>
        </div>
      </ContentWrap>
    </UploadResumeWrap>
  )
}

const FooterWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 20px;
  background: #fff;
  z-index: 12;

  .footer-btns {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const ContentWrap = styled.div`
  margin-top: 24px;

  .modal-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
    flex: 1 0 0;
    align-self: stretch;
    border-radius: 16px;
    border: 3px dashed #ebeef0;
    background: #fdfdfd;
  }

  .file-info {
    display: flex;
    padding: 32px 0;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;

    .file-name {
      color: #000;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
      margin-top: -10px;
    }
    .upload-success {
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
      color: #111212;
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 10px;
    }
  }

  .file-info img {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }

  .file-upload {
    margin-top: 24px;
    color: #1c70ee;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: auto;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }
`

const BackButton = styled.div`
  display: inline-flex;
  height: 44px;
  padding: 10px 10px 10px 0;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  p.countdownText {
    color: #111212;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
  }
`

export default UploadSuccess

const WordPdf = (
  <svg xmlns='http://www.w3.org/2000/svg' width='48' height='58' viewBox='0 0 48 58' fill='none'>
    <path
      d='M0.890625 6.77788C0.890625 3.34144 3.67641 0.555664 7.11285 0.555664H24.0017H29.7795H30.168C31.8057 0.555664 33.3773 1.20131 34.5421 2.35258L45.2647 12.9511C46.4473 14.1201 47.1129 15.7137 47.1129 17.3765V29.0001V51.2223C47.1129 54.6588 44.3271 57.4446 40.8906 57.4446H7.11286C3.67642 57.4446 0.890625 54.6588 0.890625 51.2223V6.77788Z'
      fill='#0066FF'
    />
  </svg>
)
